import { useEffect, useRef, useState } from 'react';

const rafCallbacks: {
  fn: (t: number, dt: number) => void;
  priority: number;
}[] = [];
let lastTime = 0;

/**
 * Hook for performing animations. This is the core hook for all JS driven animations
 * in the application. It is responsible for scheduling the animation callbacks and
 * allows them to be activated/deactivated based on reactive data source.
 *
 * For performing simple easing animations, see [`useApproach`].
 *
 * @param active A function or boolean value. As long as it returns true, the `fn` callback
 * will be called every display frame (using `requestAnimationFrame` API).
 *
 * @param fn The `fn` callback will be called with the current absolute time and
 * delta time since last frame during current activation. After animation was
 * deactivated, the next activation's first delta time will be 0.
 *
 * @param priority The priority specifies order at which different instances of `useRaf` will
 * have their callbacks executed. The lower the priority, the earlier the callback is called.
 *
 */
export function useRaf(
  active: boolean | (() => boolean),
  fn: (t: number, dt: number) => void,
  priority = 0
): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    const isActive = typeof active === 'function' ? active : () => active;
    if (!isActive()) {
      return;
    }

    const callback = {
      fn: (t: number, dt: number) => fnRef.current(t, dt),
      priority
    };

    const idx = rafCallbacks.findIndex((cb) => cb.priority > priority);
    if (idx >= 0) {
      rafCallbacks.splice(idx, 0, callback);
    } else {
      rafCallbacks.push(callback);
    }
    ensureRafRunning();

    return () => {
      const i = rafCallbacks.indexOf(callback);
      if (i >= 0) {
        rafCallbacks.splice(i, 1);
      }
    };
  });
}

let rafRunning = false;

function tick(time: number) {
  const delta = Math.max(0, time - lastTime);
  lastTime = time;
  if (rafCallbacks.length > 0) {
    requestAnimationFrame(tick);
    rafCallbacks.forEach((cb) => cb.fn(time, delta));
  } else {
    rafRunning = false;
  }
}

function ensureRafRunning() {
  if (!rafRunning) {
    rafRunning = true;
    lastTime = window.performance.now();
    requestAnimationFrame(tick);
  }
}

const defaultDiffFn = (a: number, b: number): number => b - a;

/**
 * Exponential approach over time.
 * http://badladns.com/stories/exp-approach
 *
 * This hook will create a state value that will be animated towards the target value
 * over time using an exponential approach.
 *
 * @param to target value to approach to
 * @param timeHorizon a factor that determines how fast the approach is.
 * @param epsilon value that determines when the animation snaps to the target.
 * @param diffFn function to calculate the difference between two values.
 */
export function useApproach(
  to: number,
  timeHorizon: number,
  epsilon = 0.005,
  diffFn = defaultDiffFn
) {
  const [current, setCurrent] = useState(to);
  const currentRef = useRef(current);
  const toRef = useRef(to);
  toRef.current = to;

  useRaf(
    () => toRef.current !== currentRef.current,
    (_, dt) => {
      const targetVal = toRef.current;
      const currentValue = currentRef.current;
      if (targetVal !== currentValue) {
        const diff = diffFn(targetVal, currentValue);
        if (Math.abs(diff) > epsilon) {
          const next = targetVal + diff / Math.exp(dt / timeHorizon);
          currentRef.current = next;
          setCurrent(next);
        } else {
          currentRef.current = targetVal;
          setCurrent(targetVal);
        }
      }
    }
  );

  function skip() {
    currentRef.current = toRef.current;
    setCurrent(toRef.current);
  }

  return { value: current, skip };
}
